import { useEffect, useState } from 'react'
import { Button, ToggleButton } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'
import type { Flash } from '../lib/counterDisplay'
import { COUNTER_SCREENS } from './counter'
import { screenChangeOf } from './counterMotion'
import { sampleNumber, useCounterStage } from './CounterStage'
import { LayoutSurface } from './render'
import { sampleBindings } from './sample'
import { screenLanguages } from './designLanguages'
import { useScreenLanguage } from '../lib/languageRotation'
import type { CounterScreen, LayoutDocument } from './types'

const SCREEN_NAMES = { called: 'designs.ed.called', free: 'designs.ed.free' } as const

/** How often the playing preview changes screen. */
const LOOP_MS = 3000

/**
 * The design as its desk's screen, with sample values. It plays Free and Called on a
 * loop, a new number each call, until paused or a screen is chosen by hand; Call again
 * recalls. Drawn by the counter display's own renderer and screen change.
 */
export function CounterPreview({
  document: doc,
  branchName,
  language: tabLanguage,
  languages,
  assetURL,
}: {
  document: LayoutDocument
  branchName: string
  language: string
  /** The design's languages, for a rotating design. */
  languages?: readonly string[]
  assetURL: (id: string) => string
}) {
  const { say } = useStaffText()
  const [screen, setScreen] = useState<CounterScreen>('called')
  const [calls, setCalls] = useState(0)
  const [flash, setFlash] = useState<Flash | null>(null)
  const [presses, setPresses] = useState(0)
  const [playing, setPlaying] = useState(true)

  const call = (recall: boolean) => {
    setCalls(recall ? calls : calls + 1)
    setScreen('called')
    setPresses(presses + 1)
    setFlash({ key: String(presses + 1), until: 0, recall })
  }

  useEffect(() => {
    if (!playing) return
    const timer = window.setTimeout(() => (screen === 'called' ? setScreen('free') : call(false)), LOOP_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, screen, calls, presses])

  // A rotating design rotates here as on the desk; otherwise the chosen tab shows.
  const plan = screenLanguages(doc, languages ?? [tabLanguage])
  const turning = useScreenLanguage(plan)
  const language = plan.rotateSec > 0 ? turning : tabLanguage
  const sample = sampleBindings(branchName, language)
  const stage = useCounterStage(screenChangeOf(doc.canvas), screen, screen === 'called' ? `called:${calls}` : 'free', flash, {
    ...sample,
    language,
    assetURL,
    preview: true,
    ticketNumber: screen === 'called' ? sampleNumber(calls) : '',
    deskPill: screen === 'called' ? 'serving' : 'open',
  })

  const byHand = (then: () => void) => {
    setPlaying(false)
    then()
  }

  return (
    <div className="studio-preview">
      <div className="studio-preview__bar" role="group" aria-label={say('designs.ed.counterScreen')}>
        <ToggleButton size="sm" isSelected={playing} onChange={setPlaying}>
          {say(playing ? 'designs.ed.pause' : 'designs.ed.play')}
        </ToggleButton>
        {COUNTER_SCREENS.map((s) => (
          <ToggleButton
            key={s}
            size="sm"
            isSelected={!playing && screen === s}
            onChange={() => byHand(() => (s === 'called' ? call(false) : setScreen('free')))}
          >
            {say(SCREEN_NAMES[s])}
          </ToggleButton>
        ))}
        <Button variant="secondary" size="sm" isDisabled={screen !== 'called'} onPress={() => byHand(() => call(true))}>
          {say('designs.ed.callAgain')}
        </Button>
      </div>
      <div className="studio-preview__screen counter-shell" {...stage.shell}>
        <LayoutSurface document={doc} screen={screen} bindings={stage.bindings} />
      </div>
    </div>
  )
}
